import React, { memo, ReactNode, useCallback, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { HandleAddSlotFrame, Slot, SlotFrameMode, SlotsModuleChildParams, useSlotsIcon } from '@bcraft-frontend-lib/slots'
import block from 'bem-cn'
import { AnimatePresence } from 'framer-motion'

import dropAndWinsPng from 'shared/img/slots/drop-and-wins-logo-white.png'
import { ReactComponent as StarSVG } from 'shared/img/slots/favourite.svg'

import { useMediaDevice } from '@bcraft-frontend-lib/core'

import { selectLocaleDict } from 'features/locale/selectors'

import { addNotify } from 'components/Notify'
import { Tooltip } from 'components/UIKit/Tooltip/Tooltip'
import { SlotPlace } from 'modules/SlotsRoot/redefinitions/types'

import { adaptSlotForIcon } from './adaptSlotForIcon'
import SlotPreview from './SlotPreview/SlotPreview'
import { SlotPreviewImage } from './SlotPreviewImage'

import './SlotsIcon.scss'

export interface ISlotsIconProps {
  slot: Slot
  wide?: boolean
  preview?: boolean
  addFrame?: HandleAddSlotFrame
  Image?: ReactNode
  className?: string
  place?: SlotPlace
}

const b = block('slots-icon')

const getPlayMode = (place: SlotPlace): SlotFrameMode =>
  place === 'free-spin' ? 'free-spin' : 'money'

const SlotsIcon: React.FC<ISlotsIconProps> = ({
  slot,
  wide,
  preview,
  addFrame,
  Image,
  className,
  place: placeProp,
}) => {
  const isMobile = useMediaDevice({ query: 'maxTablet' })
  const locale = useSelector(selectLocaleDict, shallowEqual).slots
  const { place: placeParam } = useParams<SlotsModuleChildParams<SlotPlace>>()
  const playMode = useMemo(() => getPlayMode(placeProp ?? placeParam ?? ''), [placeParam, placeProp])

  const imageDevice = isMobile ? 'mobile' : 'desktop'
  const viewModel = useMemo(() => adaptSlotForIcon(slot, imageDevice), [slot, imageDevice])
  const { slot: normalizedSlot, gameName, imageUrl, imageUrlWide } = viewModel

  const notAuthCallback = useCallback(() => {
    addNotify(locale.pleaseAuth)
  }, [locale])

  const { slotRedirect, isFavorite, onFavoriteClick, isNew, isDropAndWins, isHasDemo } = useSlotsIcon({
    slot: normalizedSlot,
    notAuthCallback,
    isMobileModal: true,
  })

  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const isCatalogSlot = 'externalId' in slot

  const onPreviewClick = useCallback(() => setIsPreviewOpen(prevState => !prevState), [])

  const playLink = useMemo(
    () => slotRedirect.linkTo(normalizedSlot.id, playMode),
    [normalizedSlot.id, playMode, slotRedirect],
  )
  const demoLink = useMemo(
    () => (isHasDemo ? slotRedirect.linkTo(normalizedSlot.id, 'demo') : undefined),
    [isHasDemo, normalizedSlot.id, slotRedirect],
  )

  const handlePlayClick = useCallback(
    (e: React.MouseEvent) => {
      slotRedirect.handleClickSlot(e, normalizedSlot.id, playMode)
    },
    [normalizedSlot.id, playMode, slotRedirect],
  )

  const handleDemoClick = useCallback(
    (e: React.MouseEvent) => {
      slotRedirect.handleClickSlot(e, normalizedSlot.id, 'demo')
    },
    [normalizedSlot.id, slotRedirect],
  )

  const handlePreviewClick = useCallback(
    (e: React.MouseEvent, isDemo = false) => {
      slotRedirect.handleClickSlot(e, normalizedSlot.id, isDemo ? 'demo' : playMode)
    },
    [normalizedSlot.id, playMode, slotRedirect],
  )

  const mixedClassName = b({ wide: !!(wide && imageUrlWide), preview }).mix(className || '').toString()
  const previewImageUrl = wide && imageUrlWide ? imageUrlWide : imageUrl

  const slotImage = useMemo(() => {
    if (Image) {
      return Image
    }

    return (
      <SlotPreviewImage
        resetKey={`${normalizedSlot.id}-${imageDevice}`}
        gameName={gameName}
        imageDevice={imageDevice}
        isCatalogSlot={isCatalogSlot}
        slotId={normalizedSlot.id}
        imageUrl={imageUrl}
        imageUrlWide={imageUrlWide}
        wide={wide}
      />
    )
  }, [
    Image,
    gameName,
    imageUrl,
    imageUrlWide,
    imageDevice,
    isCatalogSlot,
    normalizedSlot.id,
    wide,
  ])

  const handleAddFrame = useCallback(() => {
    addFrame?.({ slot: normalizedSlot, mode: playMode })
  }, [addFrame, normalizedSlot, playMode])

  if (addFrame) {
    return (
      <div className={mixedClassName}>
        <div className={b('add-frame-container')}>
          <div className={b('add-frame')} onClick={handleAddFrame}>
            +
          </div>
        </div>
      </div>
    )
  }

  const overlayContent = (
    <div className={b('overlay__content')}>
      <div className={b('overlay__title', { demo: isHasDemo })}>
        <Link className={b('overlay__button', { yellow: true })} to={playLink} onClick={handlePlayClick}>
          {locale.play}
        </Link>
        {isHasDemo && demoLink && (
          <Link className={b('overlay__button')} to={demoLink} onClick={handleDemoClick}>
            {locale.demoStart}
          </Link>
        )}
      </div>
    </div>
  )

  return (
    <div className={mixedClassName} key={normalizedSlot.id}>
      <div className={b('img-wrapper')}>
        <div className={b('img-container')}>
          {isNew ? <div className={b('label-new')}>{locale.new}</div> : null}
          <div className={b('overlay')}>{overlayContent}</div>
          <div className={b('img-content')}>
            {slotImage}
            {isDropAndWins && (
              <div className={b('dropWins-wrapper')}>
                <img src={dropAndWinsPng} alt="Drops & Wins" className={b('dropWins-image')} />
              </div>
            )}
          </div>
        </div>
        {isMobile && <div className={b('open-preview')} onClick={onPreviewClick} />}
      </div>
      <div className={b('footer')}>
        <span className={b('footer-name')}>{gameName}</span>
        <Tooltip text={isFavorite ? locale.removeFromFavorites : locale.addToFavorites}>
          <StarSVG
            onClick={onFavoriteClick}
            className={b('footer-favorite-icon', { isActive: isFavorite }).toString()}
          />
        </Tooltip>
      </div>
      <AnimatePresence>
        {isPreviewOpen && (
          <SlotPreview
            img={previewImageUrl}
            name={gameName}
            isFavorite={isFavorite}
            playRoute={playLink.pathname}
            demoRoute={demoLink?.pathname}
            clickHandler={handlePreviewClick}
            favoriteHandler={onFavoriteClick}
            close={onPreviewClick}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export default memo(SlotsIcon)
